/**
 * Authentication Store
 * CD-272: Setup authentication store
 * Manages user authentication state, tokens, and user data
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import authService from '@/services/authService'
import router from '@/router'
// F-Frontend-003: imported here so logout() can clear cross-store state.
// Verified no circular-import risk — none of these stores import auth.
import { useDomainsStore } from './domains'
import { useScansStore } from './scans'
import { useBillingStore } from './billing'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref(null)
  const token = ref(null)
  const loading = ref(false)
  const error = ref(null)
  const initialized = ref(false)

  // Registration flow state
  const registrationEmail = ref(null)
  const verificationToken = ref(null)

  // CD-362: Promise for router to await initialization
  let initPromise = null

  // Computed
  const isAuthenticated = computed(() => !!token.value)
  const isAdmin = computed(() => user.value?.role === 'admin')
  const userEmail = computed(() => user.value?.email || '')
  const userName = computed(() => user.value?.full_name || user.value?.email || '')

  /**
   * Initialize auth state from localStorage
   * CD-362: Fixed to properly restore admin sessions on page refresh
   * Returns a Promise so callers can await initialization before routing
   */
  async function init() {
    if (initPromise) return initPromise

    initPromise = (async () => {
      const savedToken = localStorage.getItem('token')
      const isAdminSession = localStorage.getItem('isAdmin') === 'true'
      if (savedToken) {
        token.value = savedToken
        if (isAdminSession) {
          await fetchAdmin()
        } else {
          await fetchUser()
        }
      }
      initialized.value = true
    })()

    return initPromise
  }

  /**
   * Wait for initialization to complete
   * CD-362: Router guard calls this to ensure auth state is ready
   */
  async function waitForInit() {
    if (initialized.value) return
    if (initPromise) await initPromise
  }

  /**
   * Login user
   * @param {string} email
   * @param {string} password
   */
  async function login(email, password) {
    loading.value = true
    error.value = null

    try {
      const response = await authService.login(email, password)
      token.value = response.access_token
      user.value = response.user
      localStorage.setItem('token', response.access_token)
      return response
    } catch (err) {
      error.value = err.detail || err.message || 'Login failed'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Login admin
   * CD-362: Now stores isAdmin flag for session persistence on page refresh
   * @param {string} email
   * @param {string} password
   */
  async function adminLogin(email, password) {
    loading.value = true
    error.value = null

    try {
      const response = await authService.adminLogin(email, password)
      token.value = response.access_token
      user.value = { ...response.admin, role: 'admin' }
      localStorage.setItem('token', response.access_token)
      localStorage.setItem('isAdmin', 'true')
      return response
    } catch (err) {
      error.value = err.detail || err.message || 'Login failed'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch current user profile
   */
  async function fetchUser() {
    if (!token.value) return null

    loading.value = true
    try {
      user.value = await authService.getCurrentUser()
      return user.value
    } catch (err) {
      // Token invalid or expired
      logout()
      return null
    } finally {
      loading.value = false
    }
  }

  /**
   * Fetch current admin profile
   * CD-362: Added for admin session persistence on page refresh
   */
  async function fetchAdmin() {
    if (!token.value) return null

    loading.value = true
    try {
      const adminData = await authService.getCurrentAdmin()
      user.value = { ...adminData, role: 'admin' }
      return user.value
    } catch (err) {
      // Token invalid or expired
      logout()
      return null
    } finally {
      loading.value = false
    }
  }

  /**
   * Logout user.
   *
   * CD-362: Also clears isAdmin flag.
   * F-Frontend-003: Also clears the other Pinia stores and stops scan
   * polling. Without this, on a shared browser tab the next user logging
   * in could see the previous user's domains, scan history, credit balance,
   * and saved payment methods through KeepAlive-cached views until those
   * views re-hydrate. The `pollingScans` Map in scansStore also kept live
   * setInterval callbacks firing authenticated requests with the now-cleared
   * token (generating 401s, but the data already in reactive state stayed).
   */
  function logout() {
    token.value = null
    user.value = null
    registrationEmail.value = null
    verificationToken.value = null
    localStorage.removeItem('token')
    localStorage.removeItem('isAdmin')

    // Clear other stores. Each is wrapped so a missing/older store can't
    // block the logout itself.
    try {
      const domainsStore = useDomainsStore()
      domainsStore.reset?.()
    } catch (e) { /* store not registered yet — harmless on initial load */ }
    try {
      const scansStore = useScansStore()
      scansStore.stopAllPolling?.()
      scansStore.reset?.()
    } catch (e) { /* same */ }
    try {
      const billingStore = useBillingStore()
      billingStore.$reset?.()
    } catch (e) { /* same */ }
  }

  /**
   * Initiate registration
   * @param {string} email
   */
  async function initiateRegistration(email) {
    loading.value = true
    error.value = null

    try {
      const response = await authService.initiateRegistration(email)
      registrationEmail.value = email
      return response
    } catch (err) {
      error.value = err.detail || err.message || 'Registration failed'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Verify OTP
   * @param {string} otp
   */
  async function verifyOtp(otp) {
    if (!registrationEmail.value) {
      throw new Error('No registration email found')
    }

    loading.value = true
    error.value = null

    try {
      const response = await authService.verifyOtp(registrationEmail.value, otp)
      verificationToken.value = response.verification_token
      return response
    } catch (err) {
      error.value = err.detail || err.message || 'OTP verification failed'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Complete registration
   * @param {string} password
   * @param {string} fullName
   */
  async function completeRegistration(password, fullName = '') {
    if (!verificationToken.value) {
      throw new Error('No verification token found')
    }

    loading.value = true
    error.value = null

    try {
      const response = await authService.completeRegistration(
        verificationToken.value,
        password,
        fullName
      )
      // Clear registration state
      registrationEmail.value = null
      verificationToken.value = null
      return response
    } catch (err) {
      error.value = err.detail || err.message || 'Registration completion failed'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Resend OTP
   */
  async function resendOtp() {
    if (!registrationEmail.value) {
      throw new Error('No registration email found')
    }

    loading.value = true
    error.value = null

    try {
      return await authService.resendOtp(registrationEmail.value)
    } catch (err) {
      error.value = err.detail || err.message || 'Failed to resend OTP'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Request password reset
   * @param {string} email
   */
  async function requestPasswordReset(email) {
    loading.value = true
    error.value = null

    try {
      return await authService.requestPasswordReset(email)
    } catch (err) {
      error.value = err.detail || err.message || 'Password reset request failed'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Reset password
   * @param {string} resetToken
   * @param {string} password
   */
  async function resetPassword(resetToken, password) {
    loading.value = true
    error.value = null

    try {
      return await authService.resetPassword(resetToken, password)
    } catch (err) {
      error.value = err.detail || err.message || 'Password reset failed'
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Set auth data directly (for testing or external auth)
   * @param {string} authToken
   * @param {object} userData
   */
  function setAuth(authToken, userData) {
    token.value = authToken
    user.value = userData
    localStorage.setItem('token', authToken)
  }

  /**
   * Clear error
   */
  function clearError() {
    error.value = null
  }

  return {
    // State
    user,
    token,
    loading,
    error,
    initialized,
    registrationEmail,
    verificationToken,

    // Computed
    isAuthenticated,
    isAdmin,
    userEmail,
    userName,

    // Actions
    init,
    waitForInit,
    login,
    adminLogin,
    fetchUser,
    fetchAdmin,
    logout,
    initiateRegistration,
    verifyOtp,
    completeRegistration,
    resendOtp,
    requestPasswordReset,
    resetPassword,
    setAuth,
    clearError
  }
})
